import { useMemo, useState } from "react";
import { TruckIcon, Search, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import {
  HEADER_BG,
  PAY_COLORS,
  PAY_LABELS,
  fmtBRL,
  monthLabel,
} from "../lib/constants";

function PayBadge({ payMethod, cartaoDesc }) {
  if (!payMethod) return <span className="text-[10px] italic text-slate-300">—</span>;
  const pc = PAY_COLORS[payMethod];
  return (
    <span
      className="text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap"
      style={{ background: pc.bg, color: pc.color, borderColor: pc.border }}
    >
      {PAY_LABELS[payMethod]}
      {payMethod === "cartao" && cartaoDesc ? ` · ${cartaoDesc}` : ""}
    </span>
  );
}

function PayEditor({ order, onSave, onCancel }) {
  const [pay, setPay] = useState(order.payMethod || "");
  const [desc, setDesc] = useState(order.cartaoDesc || "");
  return (
    <div className="flex items-center gap-1">
      <select
        autoFocus
        value={pay}
        onChange={(e) => {
          const v = e.target.value;
          setPay(v);
          if (v !== "cartao") onSave(v || null, "");
        }}
        onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}
        className="text-[10px] rounded border border-slate-300 px-1 py-0.5 focus:outline-none focus:border-blue-500 bg-white"
        data-testid={`saida-pay-select-${order.id}`}
      >
        {!order.payMethod && <option value="">Selecione…</option>}
        {Object.entries(PAY_LABELS).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
      {pay === "cartao" && (
        <input
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          onBlur={() => onSave("cartao", desc)}
          onKeyDown={(e) => e.key === "Enter" && onSave("cartao", desc)}
          placeholder="Desc."
          className="text-[10px] w-16 rounded border border-indigo-300 bg-indigo-50 text-indigo-800 px-1 py-0.5 focus:outline-none focus:border-indigo-500"
          autoFocus
        />
      )}
      <button className="text-[10px] text-slate-400" onClick={onCancel} data-testid={`saida-pay-cancel-${order.id}`}>
        ×
      </button>
    </div>
  );
}

const SortIcon = ({ active, dir }) => {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40 inline ml-1" />;
  return dir === "desc" ? <ArrowDown className="w-3 h-3 inline ml-1" /> : <ArrowUp className="w-3 h-3 inline ml-1" />;
};

const dateToNum = (br) => {
  if (!br) return 0;
  const [d, m, y] = br.split("/");
  return (parseInt(y) || 0) * 10000 + (parseInt(m) || 0) * 100 + (parseInt(d) || 0);
};
const timeToNum = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(":");
  return (parseInt(h) || 0) * 60 + (parseInt(m) || 0);
};

const HEADERS = [
  "DATA ENT.",
  "HORA ENT.",
  "DATA SAÍ.",
  "HORA SAÍ.",
  "CLIENTE",
  "APARELHO",
  "SERVIÇO",
  "VALOR (R$)",
  "CUSTO (R$)",
  "PAGAMENTO",
  "OBS.",
];

export default function SaidaView({ orders, searchQuery, onExit, onUpdateOrder }) {
  const canSeeCost = true;
  const [monthFilter, setMonthFilter] = useState("all");
  const [tab, setTab] = useState("delivered"); // "all" | "delivered" | "ready"
  const [clientSearch, setClientSearch] = useState("");
  const [editingCostId, setEditingCostId] = useState(null);
  const [editingClientId, setEditingClientId] = useState(null);
  const [editingDeviceId, setEditingDeviceId] = useState(null);
  const [editingServicesId, setEditingServicesId] = useState(null);
  const [editingValueId, setEditingValueId] = useState(null);
  const [editingObsId, setEditingObsId] = useState(null);
  const [editingPayId, setEditingPayId] = useState(null);
  const [editingExitDateId, setEditingExitDateId] = useState(null);
  const [sort, setSort] = useState({ field: null, dir: "desc" });

  const toggleSort = (field) => {
    setSort((cur) => {
      if (cur.field !== field) return { field, dir: "desc" };
      if (cur.dir === "desc") return { field, dir: "asc" };
      return { field: null, dir: "desc" };
    });
  };

  const delivered = useMemo(() => orders.filter((o) => o.status === "delivered"), [orders]);
  const ready = useMemo(() => orders.filter((o) => o.status === "ready"), [orders]);

  const list = useMemo(() => {
    if (tab === "all") return [...delivered, ...ready];
    if (tab === "ready") return ready;
    return delivered;
  }, [tab, delivered, ready]);

  const availableMonths = useMemo(() => {
    const seen = new Set();
    list.forEach((o) => {
      const src = tab === "ready" ? o.entryDate : o.exitDate || o.entryDate;
      if (src) seen.add(src.slice(3, 10));
    });
    return Array.from(seen).sort();
  }, [list, tab]);

  const filtered = useMemo(() => {
    const q = (searchQuery || "").toLowerCase();
    const cq = clientSearch.trim().toLowerCase();
    let arr = list.filter((o) => {
      if (monthFilter !== "all") {
        const src = tab === "ready" ? o.entryDate : o.exitDate || o.entryDate;
        if (src?.slice(3, 10) !== monthFilter) return false;
      }
      if (cq) {
        const match =
          o.clientName?.toLowerCase().includes(cq) ||
          o.clientPhone?.includes(clientSearch.trim());
        if (!match) return false;
      }
      if (!q) return true;
      return (
        o.clientName?.toLowerCase().includes(q) ||
        o.model?.toLowerCase().includes(q) ||
        o.imei?.toLowerCase().includes(q) ||
        o.clientPhone?.includes(q) ||
        o.orderNumber?.toLowerCase().includes(q)
      );
    });

    if (sort.field) {
      const dir = sort.dir === "asc" ? 1 : -1;
      arr = [...arr].sort((a, b) => {
        let av = 0;
        let bv = 0;
        if (sort.field === "entryDate") {
          av = dateToNum(a.entryDate) * 10000 + timeToNum(a.entryTime);
          bv = dateToNum(b.entryDate) * 10000 + timeToNum(b.entryTime);
        } else if (sort.field === "exitDate") {
          av = dateToNum(a.exitDate) * 10000 + timeToNum(a.exitTime);
          bv = dateToNum(b.exitDate) * 10000 + timeToNum(b.exitTime);
        }
        return (av - bv) * dir;
      });
    }
    return arr;
  }, [list, monthFilter, searchQuery, clientSearch, tab, sort]);

  const showReady = tab === "ready";
  const showAction = tab === "ready" || tab === "all";

  return (
    <div className="p-6 space-y-4" data-testid="saida-view">
      {/* Top row (só título) */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Saída</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {tab === "all" ? "Todos" : tab === "ready" ? "Prontos para entrega" : "Aparelhos entregues"}
            {" · "}
            {filtered.length} {filtered.length === 1 ? "item" : "itens"}
          </p>
        </div>
      </div>

      {/* Tabs + client search + month filters */}
      <div className="flex items-center flex-wrap gap-2">
        <div className="inline-flex rounded-lg bg-slate-100 p-0.5 mr-2" data-testid="saida-tabs">
          <button
            onClick={() => setTab("all")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
              tab === "all" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
            }`}
            data-testid="tab-todos"
          >
            Todos
            <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
              {delivered.length + ready.length}
            </span>
          </button>
          <button
            onClick={() => setTab("delivered")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
              tab === "delivered" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
            }`}
            data-testid="tab-entregues"
          >
            Aparelhos entregues
            <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
              {delivered.length}
            </span>
          </button>
          <button
            onClick={() => setTab("ready")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
              tab === "ready" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
            }`}
            data-testid="tab-prontos"
          >
            Prontos p/ entrega
            <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
              {ready.length}
            </span>
          </button>
        </div>

        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
          <input
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            placeholder="Buscar cliente..."
            data-testid="saida-client-search"
            className="h-9 rounded-lg border border-slate-300 pl-8 pr-3 text-sm w-56 focus:border-blue-500 focus:outline-none bg-white"
          />
        </div>
        <div className="w-px h-6 bg-slate-200 mx-1" />

        <button
          onClick={() => setMonthFilter("all")}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
            monthFilter === "all"
              ? "bg-slate-900 text-white"
              : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
          }`}
        >
          Todos os meses
        </button>
        {availableMonths.map((m) => (
          <button
            key={m}
            onClick={() => setMonthFilter(m)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
              monthFilter === m
                ? "bg-slate-900 text-white"
                : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
            }`}
          >
            {monthLabel(m)}
          </button>
        ))}
      </div>

      {/* Spreadsheet */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div
          className="px-4 py-3 text-center text-white font-heading font-bold tracking-widest text-sm uppercase"
          style={{ background: HEADER_BG }}
        >
          {showReady ? "APARELHOS PRONTOS PARA ENTREGA" : tab === "all" ? "APARELHOS DE SAÍDA (TODOS)" : "APARELHOS DE SAÍDA"} —{" "}
          {monthFilter === "all" ? "TODOS OS MESES" : monthLabel(monthFilter).toUpperCase()}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800">
                {HEADERS.map((h) => {
                  const sortable = h === "DATA ENT." ? "entryDate" : h === "DATA SAÍ." ? "exitDate" : null;
                  return (
                    <th
                      key={h}
                      onClick={sortable ? () => toggleSort(sortable) : undefined}
                      className={`text-left text-[10px] font-bold uppercase tracking-wider text-white/90 px-3 py-2.5 whitespace-nowrap border-r border-slate-700 last:border-0 ${
                        sortable ? "cursor-pointer select-none hover:bg-slate-700" : ""
                      }`}
                      data-testid={sortable ? `saida-sort-${sortable}` : undefined}
                    >
                      {h}
                      {sortable && <SortIcon active={sort.field === sortable} dir={sort.dir} />}
                    </th>
                  );
                })}
                {showAction && (
                  <th className="text-left text-[10px] font-bold uppercase tracking-wider text-white/90 px-3 py-2.5 whitespace-nowrap">
                    AÇÃO
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={HEADERS.length + (showAction ? 1 : 0)} className="text-center py-12 text-slate-400 text-sm">
                    Nada por aqui ainda.
                  </td>
                </tr>
              )}
              {filtered.map((o) => (
                <tr key={o.id} className="hover:bg-blue-50/40 border-b border-slate-100 last:border-0 transition-colors">
                  <td className="px-3 py-2.5 text-xs text-slate-700 whitespace-nowrap">{o.entryDate}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-500 tabular-nums whitespace-nowrap">{o.entryTime}</td>
                  {editingExitDateId === o.id ? (
                    <td className="px-3 py-2.5" colSpan={2}>
                      <InlineTwoInputs autoFocusFirst v1={o.exitDate || ""} v2={o.exitTime || ""} p1="dd/mm/aaaa" p2="hh:mm"
                        onSave={(a, b) => { const patch = {}; if (a !== (o.exitDate || "")) patch.exitDate = a || null; if (b !== (o.exitTime || "")) patch.exitTime = b || null; if (Object.keys(patch).length) onUpdateOrder(o.id, patch); setEditingExitDateId(null); }}
                        onCancel={() => setEditingExitDateId(null)} />
                    </td>
                  ) : (
                    <>
                      <td className="px-3 py-2.5 text-xs text-slate-700 whitespace-nowrap">
                        <button onClick={() => setEditingExitDateId(o.id)} data-testid={`saida-edit-exit-${o.id}`} className="hover:text-blue-700 text-left" title="Clique para editar data/hora de saída">
                          {o.exitDate || <span className="italic text-slate-300">— editar —</span>}
                        </button>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-500 tabular-nums whitespace-nowrap">
                        <button onClick={() => setEditingExitDateId(o.id)} data-testid={`saida-edit-exit-time-${o.id}`} className="hover:text-blue-700 text-left">
                          {o.exitTime || <span className="italic text-slate-300">—</span>}
                        </button>
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2.5">
                    {editingClientId === o.id ? (
                      <InlineTwoInputs autoFocusFirst v1={o.clientName || ""} v2={o.clientPhone || ""} p1="Nome" p2="Telefone"
                        onSave={(a, b) => { const patch = {}; if (a !== o.clientName) patch.clientName = a; if (b !== o.clientPhone) patch.clientPhone = b; if (Object.keys(patch).length) onUpdateOrder(o.id, patch); setEditingClientId(null); }}
                        onCancel={() => setEditingClientId(null)} />
                    ) : (
                      <button onClick={() => setEditingClientId(o.id)} className="text-left w-full" data-testid={`saida-edit-client-${o.id}`} title="Clique para editar">
                        <div className="text-xs font-semibold text-slate-800 hover:text-blue-700">{o.clientName || "— editar —"}</div>
                        <div className="text-[10px] text-slate-400">{o.clientPhone || "—"}</div>
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {editingDeviceId === o.id ? (
                      <InlineTwoInputs autoFocusFirst v1={o.model || ""} v2={o.color || ""} p1="Modelo" p2="Cor"
                        onSave={(a, b) => { const patch = {}; if (a !== o.model) patch.model = a; if (b !== o.color) patch.color = b; if (Object.keys(patch).length) onUpdateOrder(o.id, patch); setEditingDeviceId(null); }}
                        onCancel={() => setEditingDeviceId(null)} />
                    ) : (
                      <button onClick={() => setEditingDeviceId(o.id)} className="text-left w-full" data-testid={`saida-edit-device-${o.id}`} title="Clique para editar">
                        <div className="text-xs text-slate-700 hover:text-blue-700">{o.model || "— editar —"}</div>
                        <div className="text-[10px] text-slate-400">{o.color || "—"}</div>
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {editingServicesId === o.id ? (
                      <textarea autoFocus rows={2} defaultValue={(o.services || []).join(", ")}
                        onBlur={(e) => { const list = e.target.value.split(",").map((s) => s.trim()).filter(Boolean); const prev = o.services || []; if (list.length !== prev.length || list.some((s, i) => s !== prev[i])) onUpdateOrder(o.id, { services: list }); setEditingServicesId(null); }}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); } if (e.key === "Escape") setEditingServicesId(null); }}
                        className="w-full text-[11px] border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none resize-none"
                        data-testid={`saida-edit-services-${o.id}`} />
                    ) : (
                      <button onClick={() => setEditingServicesId(o.id)} className="text-left w-full" data-testid={`saida-services-${o.id}`} title="Clique para editar (separe por vírgula)">
                        <div className="flex flex-wrap gap-1">
                          {(o.services || []).length === 0 ? (
                            <span className="text-[10px] italic text-slate-300 hover:text-blue-600">— editar —</span>
                          ) : (o.services || []).map((s) => (
                            <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 border border-blue-100 text-blue-700 whitespace-nowrap">{s}</span>
                          ))}
                        </div>
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs font-semibold text-slate-800 tabular-nums whitespace-nowrap">
                    {editingValueId === o.id ? (
                      <input autoFocus type="text" defaultValue={o.total ? fmtBRL(o.total) : ""} placeholder="0,00"
                        onBlur={(e) => { const v = parseFloat(e.target.value.replace(/\./g, "").replace(",", ".")) || 0; if (v !== (o.total || 0)) onUpdateOrder(o.id, { total: v }); setEditingValueId(null); }}
                        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                        className="w-20 text-xs text-right border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none"
                        data-testid={`saida-edit-value-${o.id}`} />
                    ) : (
                      <button onClick={() => setEditingValueId(o.id)} className="hover:text-blue-700" data-testid={`saida-value-${o.id}`}>
                        R$ {fmtBRL(o.total)}
                      </button>
                    )}
                  </td>
                  {canSeeCost && (
                    <td className="px-3 py-2.5 text-xs tabular-nums whitespace-nowrap">
                      {editingCostId === o.id ? (
                        <input
                          autoFocus
                          type="text"
                          defaultValue={o.costValue ? fmtBRL(o.costValue) : ""}
                          placeholder="0,00"
                          onBlur={(e) => {
                            const v = parseFloat(e.target.value.replace(/\./g, "").replace(",", ".")) || 0;
                            if (v !== (o.costValue || 0)) onUpdateOrder(o.id, { costValue: v });
                            setEditingCostId(null);
                          }}
                          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                          className="w-20 text-xs text-right border border-rose-300 rounded px-1.5 py-0.5 bg-rose-50 focus:outline-none"
                          data-testid={`edit-cost-${o.id}`}
                        />
                      ) : (
                        <button
                          onClick={() => setEditingCostId(o.id)}
                          className={o.costValue ? "text-rose-700 hover:text-rose-900" : "text-slate-300 hover:text-rose-600"}
                          data-testid={`cost-${o.id}`}
                        >
                          {o.costValue ? `R$ ${fmtBRL(o.costValue)}` : "— editar —"}
                        </button>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2.5">
                    {editingPayId === o.id ? (
                      <PayEditor
                        order={o}
                        onSave={(pay, desc) => {
                          onUpdateOrder(o.id, { payMethod: pay, cartaoDesc: desc });
                          setEditingPayId(null);
                        }}
                        onCancel={() => setEditingPayId(null)}
                      />
                    ) : (
                      <button onClick={() => setEditingPayId(o.id)} data-testid={`saida-pay-btn-${o.id}`}>
                        {o.payMethod ? (
                          <PayBadge payMethod={o.payMethod} cartaoDesc={o.cartaoDesc} />
                        ) : (
                          <span className="text-[10px] italic text-slate-400 hover:text-slate-600">— selecionar —</span>
                        )}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-slate-600 max-w-[160px] truncate" title={o.observation}>
                    {editingObsId === o.id ? (
                      <input autoFocus defaultValue={o.observation || ""} placeholder="Observação..."
                        onBlur={(e) => { const v = e.target.value; if (v !== (o.observation || "")) onUpdateOrder(o.id, { observation: v }); setEditingObsId(null); }}
                        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                        className="w-full text-[11px] border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none"
                        data-testid={`saida-edit-obs-${o.id}`} />
                    ) : (
                      <button onClick={() => setEditingObsId(o.id)} className="text-left w-full hover:text-blue-700" data-testid={`saida-obs-${o.id}`}>
                        {o.observation || <span className="italic text-slate-300">— editar —</span>}
                      </button>
                    )}
                  </td>
                  {showAction && (
                    <td className="px-3 py-2.5">
                      {o.status === "ready" ? (
                        <button
                          onClick={() => onExit(o)}
                          data-testid={`saida-exit-${o.id}`}
                          className="h-8 px-3 rounded-md text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1.5"
                        >
                          <TruckIcon className="w-3.5 h-3.5" /> Entregar
                        </button>
                      ) : (
                        <span className="text-[10px] italic text-slate-300">—</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function InlineTwoInputs({ v1, v2, p1, p2, onSave, onCancel, autoFocusFirst }) {
  const [a, setA] = useState(v1 || "");
  const [b, setB] = useState(v2 || "");
  const commit = () => onSave(a, b);
  // Commit only when focus leaves the WHOLE container (not when moving between the two inputs)
  const handleBlur = (e) => {
    const next = e.relatedTarget;
    if (next && e.currentTarget.contains(next)) return; // focus still inside → don't commit
    commit();
  };
  return (
    <div className="flex flex-col gap-1"
      onBlur={handleBlur}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") onCancel(); }}>
      <input autoFocus={autoFocusFirst} value={a} onChange={(e) => setA(e.target.value)} placeholder={p1}
        className="w-full text-xs border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none" />
      <input value={b} onChange={(e) => setB(e.target.value)} placeholder={p2}
        className="w-full text-[10px] border border-blue-200 rounded px-1.5 py-0.5 bg-blue-50/50 focus:outline-none" />
    </div>
  );
}

